import debounce from 'lodash/debounce'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useParams, useNavigate } from 'react-router-dom'
import type { Playlist } from 'types/playlist'
import type { State } from 'types/redux'
import emptySearch1x from 'assets/images/empty-search.png'
import emptySearch2x from 'assets/images/empty-search@2x.png'
import { clientLibraryAlbumsUpdate } from '@disco/state-management/actions/client-library-album-actions'
import {
  batchUpdatePlaylists,
  getKeyByPlaylistType,
  loadNextPlaylists,
  searchPlaylists,
} from '@disco/state-management/actions/playlist-actions'
import { plural } from '@disco/utils/string-utils'
import AlbumGridItem from 'components/common/album-grid-item'
import PlaylistGridItem from 'components/common/playlist-grid-item'
import Row from 'components/common/row'
import DocumentHeadTags from 'components/document-head-tags'
import { Tab, Tabs } from 'components/ui'
import Button from 'components/ui/button'
import Checkbox from 'components/ui/checkbox'
import EmptyState from 'components/ui/empty-state'
import Grid from 'components/ui/grid'
import Heading from 'components/ui/heading'
import InfiniteScroll from 'components/ui/infinite-scroll'
import Input from 'components/ui/input'
import ModalWindow from 'components/ui/modal-window'

const LibrariesLibraryAlbumsAdd = (): React.ReactNode => {
  const params = useParams<{ libraryId: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const libraryId: number = parseInt(params.libraryId!, 10)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [selectedTab, setSelectedTab] = useState<number>(0)
  const [query, setQuery] = useState<string>('')
  const [isLoadingNext, setIsLoadingNext] = useState<boolean>(false)
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const playlistType: Playlist['playlist_type'] = useMemo(
    () => (selectedTab === 0 ? 'album' : 'playlist'),
    [selectedTab],
  )
  const search: Record<string, any> = useSelector((state: State) => {
    const key = getKeyByPlaylistType(playlistType)
    return state.clientLibraries.search[key]
  })
  useEffect(() => {
    setIsLoadingNext(false)
    dispatch(searchPlaylists(query, playlistType))
  }, [playlistType, selectedTab])
  const debouncedQuery = useRef(
    debounce((query: string, playlistType: Playlist['playlist_type']) => {
      setIsSearching(false)
      dispatch(searchPlaylists(query, playlistType))
    }, 300),
  ).current
  const updateSearchQuery = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsLoadingNext(false)
      setIsSearching(true)
      setQuery(event.target.value)
      debouncedQuery(event.target.value, playlistType)
    },
    [debouncedQuery, playlistType],
  )
  const updateSelectedIds = useCallback((id: number) => {
    setSelectedIds((prevState: number[]) => {
      if (prevState.includes(id)) {
        return prevState.filter((item: number) => item !== id)
      } else {
        return [...prevState, id]
      }
    })
  }, [])
  const loadNext = useCallback(
    (next: string) => {
      setIsLoadingNext(true)
      dispatch(loadNextPlaylists(next, playlistType))
    },
    [dispatch, playlistType],
  )
  const handleCancel = useCallback(
    (event: React.SyntheticEvent<any>) => {
      event.preventDefault()
      navigate(-1)
    },
    [navigate],
  )
  const submitCaption: string = useMemo(() => {
    if (playlistType === 'album') {
      return selectedIds.length > 0
        ? `Publish ${plural(selectedIds.length, 'album', 'albums')}`
        : 'Publish albums'
    }

    if (playlistType === 'playlist') {
      return selectedIds.length > 0
        ? `Convert and publish ${plural(
            selectedIds.length,
            'playlist',
            'playlists',
          )}`
        : 'Convert and publish playlists'
    }

    return ''
  }, [playlistType, selectedIds])
  const handleSubmit = useCallback(
    (event: React.SyntheticEvent<any>) => {
      event.preventDefault()
      dispatch(clientLibraryAlbumsUpdate(libraryId, selectedIds, []))

      if (playlistType === 'playlist') {
        dispatch(
          batchUpdatePlaylists(selectedIds, {
            playlist_type: 'album',
          }),
        )
      }

      navigate(-1)
    },
    [dispatch, libraryId, selectedIds, navigate],
  )

  const renderContent = () => {
    if (!isLoadingNext && search.loading) {
      return 'Loading...'
    } else if (
      !isSearching &&
      query !== '' &&
      search.loaded &&
      search.items.length === 0
    ) {
      return (
        <EmptyState>
          <img
            srcSet={`${emptySearch1x}, ${emptySearch2x} 2x`}
            width="177"
            height="160"
          />
          <p>
            Sorry! We couldn’t find anything for that search. Please try another
            query.
          </p>
        </EmptyState>
      )
    }

    return (
      <InfiniteScroll next={search.next} loadNext={loadNext}>
        {playlistType === 'album' && (
          <Grid size="medium">
            {search.items.map((item: Playlist) => {
              const isPublished: boolean = item['client_library_ids']
                ? item['client_library_ids'].includes(libraryId)
                : false
              const isSelected: boolean = selectedIds.includes(
                item['playlist_id'],
              )
              const overlayActions: React.ReactNode = !isPublished && (
                <Checkbox checked={isSelected} />
              )
              return (
                <AlbumGridItem
                  key={item['playlist_id']}
                  album={item}
                  status={isPublished ? 'Published' : undefined}
                  overlayActions={overlayActions}
                  overlayVisible={isSelected}
                  onClick={!isPublished ? updateSelectedIds : undefined}
                />
              )
            })}
          </Grid>
        )}
        {playlistType === 'playlist' && (
          <Grid size="large">
            {search.items.map((item: Playlist) => {
              const isSelected: boolean = selectedIds.includes(
                item['playlist_id'],
              )
              const overlayActions: React.ReactNode = (
                <Checkbox checked={isSelected} />
              )
              return (
                <PlaylistGridItem
                  key={item['playlist_id']}
                  playlist={item}
                  overlayActions={overlayActions}
                  overlayVisible={isSelected}
                  onClick={updateSelectedIds}
                />
              )
            })}
          </Grid>
        )}
      </InfiniteScroll>
    )
  }

  return (
    <ModalWindow
      header={
        <>
          <Heading size={700}>Publish new album</Heading>
          <div className="form-note">
            To add albums to your Catalog, browse them below or search for them,
            and click the checkbox.
          </div>
        </>
      }
      actions={
        <Button
          appearance="primary"
          caption={submitCaption}
          disabled={search.loading || selectedIds.length === 0}
          onClick={handleSubmit}
        />
      }
      isOpen={true}
      onClose={handleCancel}
    >
      <DocumentHeadTags title="Publish new album | DISCO" />
      <Row columnsSize={[3]} marginBottom="small">
        <div>
          <Input
            icon="search"
            placeholder="Search"
            value={query}
            onChange={updateSearchQuery}
          />
        </div>
      </Row>
      <Tabs
        className="Tabs--modal"
        index={selectedTab}
        onChange={(index: number) => setSelectedTab(index)}
      >
        <Tab label="Albums">{renderContent()}</Tab>
        <Tab label="Playlists">{renderContent()}</Tab>
      </Tabs>
    </ModalWindow>
  )
}

export default LibrariesLibraryAlbumsAdd
